import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// components/common/FormInputs.tsx
import { useState, useRef, useEffect, useCallback, useMemo, forwardRef } from "react";
import ReactDOM from "react-dom";
import { ChevronDown, ChevronUp, Check, Search, X, Eye, EyeOff, Mail, Lock } from "lucide-react";
import "./FormInputs.css";
import MultiSelectInput from "./MultiSelectInput";
export { MultiSelectInput };
export const TextInput = forwardRef(({ label, error = false, errorMessage, required = false, disabled = false, size = "md", fullWidth = true, className = "", helperText, leftIcon, rightIcon, onRightIconClick, clearable = false, onClear, value, onChange, ...rest }, ref) => {
    const hasValue = Boolean(value);
    const handleClear = (e) => {
        e.stopPropagation();
        if (onClear) {
            onClear();
        }
        else if (onChange) {
            const syntheticEvent = {
                target: { value: "" },
            };
            onChange(syntheticEvent);
        }
    };
    const renderLeftIcon = () => {
        if (!leftIcon)
            return null;
        if (typeof leftIcon === "string") {
            return _jsx("img", { src: leftIcon, alt: "", className: "input-icon input-icon-left" });
        }
        return _jsx("span", { className: "input-icon input-icon-left", children: leftIcon });
    };
    return (_jsxs("div", { className: `form-input-wrapper ${fullWidth ? "full-width" : ""} ${className}`, children: [label && (_jsxs("label", { className: "form-input-label", children: [label, required && _jsx("span", { className: "required-asterisk", children: "*" })] })), _jsxs("div", { className: `
                input-container 
                input-${size} 
                ${error ? "input-error" : ""} 
                ${disabled ? "input-disabled" : ""}
                ${leftIcon ? "has-left-icon" : ""}
                ${rightIcon || clearable ? "has-right-icon" : ""}
            `, children: [renderLeftIcon(), _jsx("input", { ref: ref, className: "form-input", value: value, onChange: onChange, disabled: disabled, ...rest }), ((clearable && hasValue && !disabled) || rightIcon) && (_jsxs("div", { className: "input-right-section", children: [clearable && hasValue && !disabled && (_jsx(X, { size: 16, className: "input-clear-icon", onClick: handleClear })), rightIcon && (_jsx("span", { className: `input-icon input-icon-right ${onRightIconClick ? "clickable" : ""}`, onClick: onRightIconClick, children: rightIcon }))] }))] }), error && errorMessage && (_jsx("span", { className: "input-error-text", children: errorMessage })), !error && helperText && (_jsx("span", { className: "input-helper-text", children: helperText }))] }));
});
TextInput.displayName = "TextInput";
export const EmailInput = forwardRef((props, ref) => {
    return (_jsx(TextInput, { ref: ref, type: "email", leftIcon: _jsx(Mail, { size: 18 }), placeholder: "e.g. lorem@gmail.com", ...props }));
});
EmailInput.displayName = "EmailInput";
export const PasswordInput = forwardRef(({ showToggle = true, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    return (_jsx(TextInput, { ref: ref, type: showPassword ? "text" : "password", leftIcon: _jsx(Lock, { size: 18 }), rightIcon: showToggle ? (showPassword ? _jsx(EyeOff, { size: 18 }) : _jsx(Eye, { size: 18 })) : undefined, onRightIconClick: showToggle ? () => setShowPassword(!showPassword) : undefined, ...props }));
});
PasswordInput.displayName = "PasswordInput";
export const SearchInput = forwardRef(({ onSearch, debounceMs = 300, value, onChange, clearable = true, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value || "");
    const timeoutRef = useRef(null);
    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value]);
    const handleChange = (e) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        if (onChange) {
            onChange(e);
        }
        if (onSearch) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                onSearch(newValue);
            }, debounceMs);
        }
    };
    const handleClear = () => {
        setInternalValue("");
        if (onSearch)
            onSearch("");
    };
    return (_jsx(TextInput, { ref: ref, value: internalValue, onChange: handleChange, leftIcon: _jsx(Search, { size: 18 }), clearable: clearable, onClear: handleClear, placeholder: "Search...", ...props }));
});
SearchInput.displayName = "SearchInput";
export const NumberInput = forwardRef(({ min, max, step = 1, allowDecimal = true, onChange, ...props }, ref) => {
    const handleChange = (e) => {
        let value = e.target.value;
        // Allow empty, minus sign, or decimal point during typing
        if (value === "" || value === "-" || value === ".") {
            if (onChange)
                onChange(e);
            return;
        }
        // Validate number format
        const regex = allowDecimal ? /^-?\d*\.?\d*$/ : /^-?\d*$/;
        if (!regex.test(value))
            return;
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            if (min !== undefined && numValue < min)
                return;
            if (max !== undefined && numValue > max)
                return;
        }
        if (onChange)
            onChange(e);
    };
    return (_jsx(TextInput, { ref: ref, type: "text", inputMode: "decimal", onChange: handleChange, ...props }));
});
NumberInput.displayName = "NumberInput";
export const TextArea = forwardRef(({ label, error = false, errorMessage, required = false, disabled = false, size = "md", fullWidth = true, className = "", helperText, resize = "vertical", minRows = 3, maxRows, autoGrow = false, value, onChange, ...rest }, ref) => {
    const textareaRef = useRef(null);
    const combinedRef = (node) => {
        textareaRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    };
    useEffect(() => {
        if (autoGrow && textareaRef.current) {
            textareaRef.current.style.height = "auto";
            const scrollHeight = textareaRef.current.scrollHeight;
            const lineHeight = parseInt(getComputedStyle(textareaRef.current).lineHeight);
            const maxHeight = maxRows ? maxRows * lineHeight : Infinity;
            textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, [value, autoGrow, maxRows]);
    return (_jsxs("div", { className: `form-input-wrapper ${fullWidth ? "full-width" : ""} ${className}`, children: [label && (_jsxs("label", { className: "form-input-label", children: [label, required && _jsx("span", { className: "required-asterisk", children: "*" })] })), _jsx("textarea", { ref: combinedRef, className: `
                    form-textarea 
                    textarea-${size}
                    ${error ? "input-error" : ""}
                    ${disabled ? "input-disabled" : ""}
                `, style: { resize: autoGrow ? "none" : resize }, rows: minRows, value: value, onChange: onChange, disabled: disabled, ...rest }), error && errorMessage && (_jsx("span", { className: "input-error-text", children: errorMessage })), !error && helperText && (_jsx("span", { className: "input-helper-text", children: helperText }))] }));
});
TextArea.displayName = "TextArea";
// Helper function add karo file mein (Dropdown component se pehle)
const truncateText = (text, maxWords = 10) => {
    const words = text.split(' ');
    if (words.length > maxWords) {
        return words.slice(0, maxWords).join(' ') + '...';
    }
    return text;
};
export const Dropdown = ({ label, value, options, onChange, placeholder = "Select...", multiple = false, searchable = false, clearable = false, error = false, errorMessage, required = false, disabled = false, size = "md", fullWidth = true, className = "", helperText, leftIcon, loading = false, closeOnSelect, maxHeight = 250, renderOption, displayValue, rightIcon, overflowY = "auto", usePortal = false, hideChevron = false, sortByLabel = true, hideCheckIcon = false, onCreate, menuHeader, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const searchInputRef = useRef(null);
    const [portalStyle, setPortalStyle] = useState({});
    const shouldCloseOnSelect = closeOnSelect ?? !multiple;
    const selectedValues = useMemo(() => {
        if (multiple) {
            return Array.isArray(value) ? value : [];
        }
        return value ? [value] : [];
    }, [value, multiple]);
    const filteredOptions = useMemo(() => {
        let result = options;
        // ✅ Filter by search text
        if (searchText.trim()) {
            const lowerSearch = searchText.toLowerCase();
            result = result.filter((opt) => (opt.label ?? '').toLowerCase().includes(lowerSearch) ||
                (opt.value ?? '').toLowerCase().includes(lowerSearch));
        }
        // ✅ Sort A to Z by label, but keep empty value or "All" at the top
        if (sortByLabel) {
            result = [...result].sort((a, b) => {
                const aLabel = (a.label ?? '').toLowerCase();
                const bLabel = (b.label ?? '').toLowerCase();
                const aIsFirst = a.value === "" || aLabel === "all" || aLabel === "view all";
                const bIsFirst = b.value === "" || bLabel === "all" || bLabel === "view all";
                if (aIsFirst && !bIsFirst)
                    return -1;
                if (bIsFirst && !aIsFirst)
                    return 1;
                return aLabel.localeCompare(bLabel);
            });
        }
        return result;
    }, [options, searchText, sortByLabel]);
    const displayText = useMemo(() => {
        if (selectedValues.length === 0)
            return "";
        if (multiple) {
            const selectedLabels = selectedValues
                .map((val) => options.find((opt) => opt.value === val)?.label)
                .filter(Boolean);
            if (selectedLabels.length === 0)
                return "";
            if (selectedLabels.length === 1)
                return selectedLabels[0];
            if (selectedLabels.length === 2)
                return selectedLabels.join(", ");
            return `${selectedLabels[0]}, ${selectedLabels[1]} +${selectedLabels.length - 2}`;
        }
        return options.find((opt) => opt.value === selectedValues[0])?.label || "";
    }, [selectedValues, options, multiple]);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleClickOutside = (event) => {
            const target = event.target;
            const inTrigger = dropdownRef.current?.contains(target) || triggerRef.current?.contains(target);
            const inMenu = menuRef.current?.contains(target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setSearchText("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, usePortal]);
    useEffect(() => {
        if (!isOpen || !usePortal || !triggerRef.current)
            return;
        const updatePosition = () => {
            if (!triggerRef.current)
                return;
            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = maxHeight || 250;
            const showUpwards = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
            setPortalStyle({
                position: "fixed",
                top: showUpwards ? "auto" : rect.bottom + 4,
                bottom: showUpwards ? window.innerHeight - rect.top + 4 : "auto",
                left: rect.left,
                width: rect.width,
                zIndex: 99999,
            });
        };
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isOpen, usePortal, maxHeight]);
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
        }
    }, [isOpen, searchable]);
    const handleSelect = useCallback((optionValue, optionDisabled) => {
        if (optionDisabled)
            return;
        if (multiple) {
            const newValues = selectedValues.includes(optionValue)
                ? selectedValues.filter((v) => v !== optionValue)
                : [...selectedValues, optionValue];
            onChange(newValues);
        }
        else {
            onChange(optionValue);
        }
        if (shouldCloseOnSelect) {
            setIsOpen(false);
            setSearchText("");
        }
    }, [multiple, selectedValues, onChange, shouldCloseOnSelect]);
    const handleClear = useCallback((e) => {
        e.stopPropagation();
        onChange(multiple ? [] : "");
        setSearchText("");
    }, [multiple, onChange]);
    const handleToggle = useCallback(() => {
        if (!disabled && !loading) {
            const opening = !isOpen;
            if (opening && usePortal && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = maxHeight || 250;
                const showUpwards = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
                setPortalStyle({
                    position: "fixed",
                    top: showUpwards ? "auto" : rect.bottom + 4,
                    bottom: showUpwards ? window.innerHeight - rect.top + 4 : "auto",
                    left: rect.left,
                    width: rect.width,
                    zIndex: 99999,
                });
            }
            setIsOpen((prev) => !prev);
            if (isOpen)
                setSearchText("");
        }
    }, [disabled, loading, isOpen, usePortal, maxHeight]);
    return (_jsxs("div", { ref: dropdownRef, className: `form-input-wrapper ${fullWidth ? "full-width" : ""} ${className}`, children: [label && (_jsxs("label", { className: "form-input-label", children: [label, required && _jsx("span", { className: "required-asterisk", children: "*" })] })), _jsxs("div", { ref: triggerRef, className: `
                    dropdown-trigger
                    input-${size}
                    ${isOpen ? "dropdown-open" : ""}
                    ${error ? "input-error" : ""}
                    ${disabled ? "input-disabled" : ""}
                `, onClick: handleToggle, children: [leftIcon && (_jsx("img", { src: leftIcon, alt: "", className: "input-icon input-icon-left" })), _jsx("span", { className: `all-Font-Sizes dropdown-value ${!displayText ? "dropdown-placeholder" : ""}`, style: { fontSize: '13px' }, children: displayValue || displayText || placeholder }), _jsxs("div", { className: "all-Font-Sizes input-right-section", children: [rightIcon, loading && _jsx("span", { className: "dropdown-spinner" }), clearable && selectedValues.length > 0 && !disabled && (_jsx(X, { size: 16, className: "input-clear-icon", onClick: handleClear })), !hideChevron && (isOpen ? (_jsx(ChevronUp, { size: 18, className: "dropdown-chevron" })) : (_jsx(ChevronDown, { size: 18, className: "dropdown-chevron" })))] })] }), isOpen && (() => {
                const menuContent = (_jsxs("div", { ref: menuRef, className: "dropdown-menu", style: usePortal ? { ...portalStyle, maxHeight, overflowY: "auto" } : { maxHeight, overflowY: "auto" }, children: [menuHeader && (_jsx("div", { className: "dropdown-menu-header", style: { padding: '0px' }, children: menuHeader })), searchable && (_jsxs("div", { className: "dropdown-search-wrapper", children: [_jsxs("div", { style: { display: "flex", alignItems: "center", gap: "8px" }, children: [_jsx(Search, { size: 16, className: "dropdown-search-icon" }), _jsx("input", { ref: searchInputRef, type: "text", value: searchText, onChange: (e) => setSearchText(e.target.value), placeholder: "Search...", className: "dropdown-search-input", onClick: (e) => e.stopPropagation() })] }), searchText && (_jsx(X, { size: 14, className: "dropdown-search-clear", onClick: (e) => {
                                        e.stopPropagation();
                                        setSearchText("");
                                    } }))] })), _jsxs("div", { className: "dropdown-options-list", children: [filteredOptions.length === 0 && !onCreate ? (_jsx("div", { className: "dropdown-no-options", children: "No options found" })) : filteredOptions.length === 0 && onCreate ? null : (filteredOptions.map((option) => {
                                    const isSelected = selectedValues.includes(option.value);
                                    const isDisabled = option.disabled;
                                    if (renderOption) {
                                        return (_jsx("div", { className: `dropdown-option ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`, onClick: () => handleSelect(option.value, isDisabled), children: renderOption(option, isSelected) }, option.value));
                                    }
                                    return (_jsxs("div", { className: `dropdown-option ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`, onClick: () => handleSelect(option.value, isDisabled), children: [multiple && (_jsx("span", { className: `dropdown-checkbox ${isSelected ? "checked" : ""}`, children: isSelected && _jsx(Check, { size: 12 }) })), option.icon && (_jsx("img", { src: option.icon, alt: "", className: "dropdown-option-icon" })), _jsx("span", { className: "dropdown-option-label all-Font-Sizes", children: option.label }), !multiple && isSelected && !hideCheckIcon && (_jsx(Check, { size: 16, className: "dropdown-check-icon" }))] }, option.value));
                                })), onCreate && searchText.trim() && (_jsxs("div", { className: "dropdown-option", style: { color: '#7F56D9', fontWeight: 600, borderTop: filteredOptions.length > 0 ? '1px solid #EAECF0' : 'none' }, onClick: (e) => {
                                        e.stopPropagation();
                                        onCreate(searchText.trim());
                                        setSearchText('');
                                        setIsOpen(false);
                                    }, children: ["+ Create \u00A0", _jsx("span", { style: { fontWeight: 400 }, children: searchText.trim() })] }))] })] }));
                return usePortal ? ReactDOM.createPortal(menuContent, document.body) : menuContent;
            })(), error && errorMessage && (_jsx("span", { className: "input-error-text", children: errorMessage })), !error && helperText && (_jsx("span", { className: "input-helper-text", children: helperText }))] }));
};
export const FormField = ({ children, className = "", fullWidth = true, }) => {
    return (_jsx("div", { className: `form-field ${fullWidth ? "full-width" : ""} ${className}`, children: children }));
};
export const FormRow = ({ children, columns = 2, gap = 16, className = "", }) => {
    return (_jsx("div", { className: `form-row ${className}`, style: {
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: `${gap}px`
        }, children: children }));
};
export const InputGroup = ({ children, leftAddon, rightAddon, className = "", }) => {
    return (_jsxs("div", { className: `input-group ${className}`, children: [leftAddon && _jsx("div", { className: "input-addon input-addon-left", children: leftAddon }), children, rightAddon && _jsx("div", { className: "input-addon input-addon-right", children: rightAddon })] }));
};
import CustomMonthPickerBase from "./daterangepicker/CustomMonthPicker";
import { Button } from "@mui/material";
export const MonthPicker = ({ label, required = false, error = false, errorMessage, helperText, value, onChange, placeholder = "Select Month", fullWidth = true, className = "", showAllTime, futureMonths, onAllTimeYear, year, }) => {
    return (_jsxs("div", { className: `form-input-wrapper ${fullWidth ? "full-width" : ""} ${className}`, children: [label && (_jsxs("label", { className: "form-input-label", children: [label, required && _jsx("span", { className: "required-asterisk", children: "*" })] })), _jsx(CustomMonthPickerBase, { value: value, onChange: onChange, placeholder: placeholder, showAllTime: showAllTime, futureMonths: futureMonths, onAllTimeYear: onAllTimeYear, year: year }), error && errorMessage && (_jsx("span", { className: "input-error-text", children: errorMessage })), !error && helperText && (_jsx("span", { className: "input-helper-text", children: helperText }))] }));
};
export const ExportButton = ({ onClick, loading = false, disabled = false, label = "Export" }) => {
    return (_jsx(Button, { variant: "outlined", onClick: (e) => onClick(e), disabled: disabled || loading, startIcon: _jsx("img", { src: "/assets/export.svg", alt: "" }), className: "all-Font-Sizes-12", title: label, sx: {
            fontSize: '13px !important',
            color: "#181D27",
            borderColor: "#D0D5DD",
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "8px",
            height: "36px",
            px: 2,
            '&.Mui-disabled': {
                backgroundColor: '#F3F4F6',
                borderColor: '#E5E7EB',
                color: '#9CA3AF',
            },
        }, children: label }));
};
/* ================= EXPORTS ================= */
export default {
    TextInput,
    EmailInput,
    PasswordInput,
    SearchInput,
    NumberInput,
    TextArea,
    Dropdown,
    FormField,
    FormRow,
    InputGroup,
    MonthPicker,
    ExportButton
};
